import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import RangeToolbar, { RefreshControl, useDateRange } from "./RangeToolbar";
import PowerGeneration from "./EnergyInsight/PowerGeneration";
import EnergyMonitoring from "./EnergyInsight/EnergyMonitoring";
import EnergyTimelineChart from "./EnergyInsight/EnergyTimelineChart";
import GroupSummary from "./EnergyInsight/GroupSummary";
import DeviceEnergyCard from "./EnergyInsight/DeviceEnergyCard";
import HelpTip from "./EnergyInsight/HelpTip";
import { getEnergyStateMessage } from "./EnergyInsight/energyStateMessages";
import {
  logEnergyDiagnostic,
  logEnergyDiagnosticError,
} from "./EnergyInsight/energyDiagnostics";
import axios from "../../../lib/http/axiosConfig";
import { useDemoScenario } from "../../../demo/DemoScenarioContext";
import { buildForecastEnergyOverview } from "../../../utils/forecastEnergy";
import {
  findFog,
  formatAgo,
  normalizeOverviewEnergy,
  sortFogFirst,
} from "../../../utils/energyDevices";
import { useT } from "../../../i18n";
import "./Data.css";

// 에너지 탭. 원래 조회(지금의 작물분석) 탭 아래쪽에 병해충과 같이 있던 걸 탭으로 뗐다.
// /api/dashboard/{farmId}/overview 를 그대로 쓴다 — 응답의 energy 를 { mode, devices[], group } 으로 읽는다.
// BE 가 아직 옛 모양(device + energy.latest/daily)을 주면 normalizeOverviewEnergy 가 장치 1대 목록으로 바꾼다.
//
// 구성(mode)에 따라 배치가 갈린다.
//   POLE      : 스마트폴 1대. 현재 발전 타일(합계 · 태양광 · 풍력 · 소비 · 배터리) → 타임라인 차트(태양광 · 풍력 · 소비) → 추이.
//   EDGE_ONLY : 큐브 여러 대, 공유 없음. 큐브마다 카드 1장.
//   FOG       : fog 1 + edge. 그룹 합계 카드 → fog 카드 → edge 카드.
//   NONE      : 연결된 장치 없음.

// 데모 계정도 같은 화면을 쓴다. 예전에는 DemoEnergy 라는 별도 화면이 따로 있었는데,
// 실계정과 UI 가 달라 캡처마다 두 화면을 따로 확인해야 했다. 지금은 데이터만 갈린다.
export default function Energy({ active = true }) {
  const t = useT();
  const { isDemo, energyConfig, demoRegisteredDevices } = useDemoScenario();
  const farmId = useSelector((state) => state.farm.farmId);
  // 기간은 맨 아래 "에너지 추이"(일별 kWh) 차트에만 걸린다. 실시간 숫자와 실측+예측 차트는 늘 "지금" 기준.
  // 그래서 기간 버튼도 폴 · 큐브 구분 없이 그 카드 안에 둔다. 기본 7일 — 1일은 일별 집계가 1점이라 선이 안 그려진다.
  const dateRange = useDateRange("7", { autoAdvance: true });
  const { from, to, reloadToken } = dateRange;

  const [overview, setOverview] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  // 문구 대신 i18n 키를 들고 있다가 렌더 때 번역한다. 언어를 바꿔도 재조회하지 않도록.
  const [fetchError, setFetchError] = useState("");
  const [fetchedAt, setFetchedAt] = useState(null);
  // 큐브 카드 접기/펼치기. null 이면 기본값(3대 이하 전부 펼침, 4대부터 전부 접힘)을 쓴다.
  const [openSns, setOpenSns] = useState(null);
  const [pageVisible, setPageVisible] = useState(() => document.visibilityState !== "hidden");

  useEffect(() => {
    const update = () => setPageVisible(document.visibilityState !== "hidden");
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);

  useEffect(() => {
    if (!active || !pageVisible) {
      setIsLoading(false);
      return undefined;
    }
    setOpenSns(null);
    if (!isDemo && !farmId) {
      setOverview(null);
      setIsLoading(false);
      logEnergyDiagnostic("overview-fetch-skipped", { reason: "missing-farm-id" });
      return undefined;
    }

    let cancelled = false;
    const endpoint = isDemo ? "/api/energy/forecast" : `/api/dashboard/${farmId}/overview`;
    if (isDemo) setOverview(buildForecastEnergyOverview(energyConfig, null, null, null, demoRegisteredDevices));

    const load = async () => {
      setIsLoading(true);
      setFetchError("");
      logEnergyDiagnostic("overview-fetch-start", { farmId, endpoint, from, to });

      try {
        const params = isDemo ? { scenario: energyConfig === "POLE" ? "TIPS" : "GOVTECH" } : { from, to };
        // 데모는 예측과 실측을 따로 가져온다. 폴 실측은 EnergyForecast 가 올린 TIPS 값,
        // 큐브 실측은 BE 가 BigQuery 측정 테이블(시뮬레이터 또는 실제 큐브)에서 직접 읽은 값.
        const measurementsEndpoint = !isDemo ? null
          : energyConfig === "POLE" ? "/api/energy/tips/measurements" : "/api/energy/govtech/measurements";
        const [result, measured] = await Promise.allSettled([
          axios.get(endpoint, { params }),
          ...(measurementsEndpoint ? [axios.get(measurementsEndpoint)] : []),
        ]);
        if (cancelled) return;

        if (result.status === "rejected" && (!measured || measured.status === "rejected")) throw result.reason;
        const data = result.status === "fulfilled" ? result.value.data : null;
        const measurements = measured?.status === "fulfilled" ? measured.value.data?.data : null;

        const payload = isDemo
          ? buildForecastEnergyOverview(energyConfig, data?.data ?? null, measurements, { from, to }, demoRegisteredDevices)
          : data?.data ?? null;
        setOverview(payload);
        setFetchedAt(new Date());
        if (result.status === "rejected" || measured?.status === "rejected" || measurements?.status === "ERROR") {
          setFetchError("energy.fetch.failed");
        }

        const normalized = normalizeOverviewEnergy(payload);
        logEnergyDiagnostic("overview-fetch-success", {
          farmId,
          endpoint,
          mode: normalized.mode,
          deviceCount: normalized.devices.length,
          deviceSns: normalized.devices.map((device) => device.deviceSn || null),
          hasGroup: Boolean(normalized.group),
        });
      } catch (error) {
        if (cancelled) return;
        setOverview(isDemo ? buildForecastEnergyOverview(energyConfig, null, null, null, demoRegisteredDevices) : null);
        setFetchError("energy.fetch.failed");
        logEnergyDiagnosticError("overview-fetch-failed", error, { farmId, endpoint, from, to });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    // 데모는 폴 · 큐브 모두 1분. 실측이 BE 에서 1분 캐시로 오고, 예측은 매시 45분에만 바뀐다.
    const timer = isDemo ? setInterval(load, 60 * 1000) : null;

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
  }, [active, pageVisible, farmId, from, to, reloadToken, isDemo, energyConfig, demoRegisteredDevices]);

  const energy = useMemo(() => (overview ? normalizeOverviewEnergy(overview) : null), [overview]);
  const mode = energy?.mode ?? "NONE";
  const devices = useMemo(() => sortFogFirst(energy?.devices ?? []), [energy]);
  const group = energy?.group ?? null;
  const fog = mode === "FOG" ? findFog(devices) : null;
  const edges = fog ? devices.filter((device) => device !== fog) : devices;

  const defaultOpen = devices.length <= 3;
  const isCardOpen = (sn) => (openSns ? openSns.has(sn) : defaultOpen);
  const toggleCard = (sn) =>
    setOpenSns((prev) => {
      const next = new Set(prev ?? (defaultOpen ? devices.map((device) => device.deviceSn) : []));
      if (next.has(sn)) next.delete(sn);
      else next.add(sn);
      return next;
    });

  // 추이 차트의 장치 선택. fog 구성은 그룹 합계를 맨 앞에 둔다.
  const monitoringSeries = useMemo(() => {
    if (mode === "POLE" || mode === "NONE") return null;
    const items = devices.map((device) => ({
      key: device.deviceSn,
      label: device.name || device.deviceSn,
      daily: device.daily ?? [],
    }));
    if (group && (isDemo || group.daily?.length)) {
      items.unshift({ key: "__group", label: t("energy.monitoring.series.group"), daily: group.daily });
    }
    return items;
  }, [devices, group, mode, isDemo, t]);

  // 스마트폴: 지금까지와 같은 판정으로 안내 문구를 고른다.
  const pole = mode === "POLE" ? devices[0] : null;
  const poleLatest = pole?.latest ?? null;
  const poleStateType = !pole?.deviceSn
    ? "missing-identifier"
    : !poleLatest || pole.operatingStatus === false || poleLatest.hasData === false
      ? "no-incoming-data"
      : null;
  const poleStatusMessage = pole && !isDemo ? getEnergyStateMessage(poleStateType, "POLE", t) : null;
  const poleAgo = poleLatest?.hasData ? formatAgo(poleLatest.timestamp, t) : null;

  let chipLabel;
  let chipOn;
  // 칩의 점은 에너지 데이터가 오고 있는지(latest.hasData). heartbeat 연결 상태는 정보 탭 몫.
  if (mode === "POLE") {
    chipLabel = `${t("energy.device.POLE")}${pole?.deviceSn ? ` ${pole.deviceSn}` : ` ${t("energy.device.unregistered")}`}${poleAgo ? ` · ${poleAgo}` : ""}`;
    chipOn = Boolean(poleLatest?.hasData);
  } else if (mode === "NONE") {
    chipLabel = t("energy.mode.NONE");
    chipOn = false;
  } else {
    chipLabel = t("energy.device.count", { device: t("energy.device.CUBE"), n: devices.length });
    chipOn = devices.some((device) => device.latest?.hasData);
  }

  // 기간 줄에는 기간 버튼만. "HH:MM 기준 · 새로고침"은 탭 전체를 다시 불러오므로 맨 위 제목 줄에 둔다.
  const trendToolbar = (
    <RangeToolbar dateRange={dateRange} fetchedAt={fetchedAt} isLoading={isLoading} showRefresh={false} />
  );

  return (
    <div className="dt-wrapper">
      {fetchError && <div className="dt-error">{t(fetchError)}</div>}

      <div className="dt-header ei-header">
        {t("energy.tab.title")}
        {/* fog 구성에서만: 그룹 합계 카드 · fog 카드 · edge 카드가 뭐가 다른지 "?" 로 설명 */}
        {mode === "FOG" && <HelpTip label={t("energy.help.label")} lines={[t("energy.help.fogGroup"), t("energy.help.surplus")]} />}
        <RefreshControl fetchedAt={fetchedAt} isLoading={isLoading} onReload={dateRange.reload} />
      </div>

      {/* ── 장비 상태 ── */}
      <div className="dt-chips">
        <span className="dt-chip">
          <i className={`dt-dot${chipOn ? "" : " off"}`} />
          {chipLabel}
        </span>
        {mode !== "POLE" && mode !== "NONE" && (
          <span className="dt-chip">{t(`energy.mode.${mode}`)}</span>
        )}
      </div>

      <div className="dt-container">
        {!energy ? (
          <EnergyMonitoring daily={[]} isLoading={isLoading} toolbar={trendToolbar} />
        ) : mode === "NONE" ? (
          <div className="panel ei-panel">
            <div className="ei-empty-message">{t("energy.state.noDevice")}</div>
          </div>
        ) : mode === "POLE" ? (
          <>
            <PowerGeneration latest={poleLatest} statusMessage={poleStatusMessage} />
            {/* 폴은 태양광 · 풍력 · 소비를 한 차트에(각 실선 + 예측 점선).
                소비는 하나 — 두 발전이 배터리 하나로 모여 태양광 몫 · 풍력 몫으로 나눌 수 없다. */}
            <EnergyTimelineChart history={pole.history} forecast={pole.forecast} metric="pole"
              deviceLabel={`${t("energy.device.POLE")}${pole.deviceSn ? ` ${pole.deviceSn}` : ""}`}
              forecastHistory={pole.forecastHistory}
              comparisonHour={pole.comparisonHour}
              forecastOrigin={pole.forecastOrigin} consumptionPending={pole.consumptionPending} />
            <EnergyMonitoring
              daily={pole.daily ?? []}
              statusMessage={poleStatusMessage}
              isLoading={isLoading}
              toolbar={trendToolbar}
              showEmptyChart={isDemo}
              from={from}
              to={to}
            />
          </>
        ) : (
          <>
            {mode === "EDGE_ONLY" && <div className="ei-note">{t("energy.edgeOnly.note")}</div>}
            {mode === "FOG" && group && <GroupSummary group={group} fog={fog} devices={devices} showPlaceholders={isDemo} />}
            {/* fog 카드가 맨 위, edge 카드가 그 아래. 구분 제목은 두지 않는다 — 카드마다 FOG/EDGE 배지가 있어 중복이다. */}
            {fog && (
              <DeviceEnergyCard
                device={fog}
                mode={mode}
                showPlaceholders={isDemo}
                open={isCardOpen(fog.deviceSn)}
                onToggle={() => toggleCard(fog.deviceSn)}
              />
            )}
            {edges.map((device) => (
              <DeviceEnergyCard
                key={device.deviceSn}
                device={device}
                mode={mode}
                showPlaceholders={isDemo}
                open={isCardOpen(device.deviceSn)}
                onToggle={() => toggleCard(device.deviceSn)}
              />
            ))}
            <EnergyMonitoring series={monitoringSeries} isLoading={isLoading} toolbar={trendToolbar}
              showEmptyChart={isDemo} from={from} to={to} />
          </>
        )}
      </div>
    </div>
  );
}
